import { useEffect, useState } from 'react';

/** Seconds remaining until `deadlineMs` (epoch ms). Recomputed from the absolute
 *  deadline each tick, so it stays correct across re-renders and tab backgrounding. */
export function useCountdown(deadlineMs: number | null): number {
  // Normalize null/NaN to null so an invalid deadline can't drive a render loop.
  const deadline = deadlineMs != null && Number.isFinite(deadlineMs) ? deadlineMs : null;
  const compute = () =>
    deadline == null ? 0 : Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  const [secondsLeft, setSecondsLeft] = useState(compute);
  const [trackedDeadline, setTrackedDeadline] = useState(deadline);

  // Resync immediately when the deadline changes — adjust state during render
  // (the React-recommended pattern) rather than calling setState inside an effect.
  if (trackedDeadline !== deadlineMs) {
    setTrackedDeadline(deadlineMs);
    setSecondsLeft(compute());
  }

  useEffect(() => {
    if (deadline == null) return undefined;
    const id = setInterval(() => {
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsLeft(next);
      if (next === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  return secondsLeft;
}
